import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { 
  getCellIndices, 
  determineCourtForGame, 
  renderGroupTable,
  processGroupData
} from './ScheduleUtils';
import { processBracketData } from './BracketUtils';
import SearchableBracketWrapper from './SearchableBracketWrapper';

const MensSchedule = ({ activeSubTab, workbook }) => {
  const [menData, setMenData] = useState({});
  const [groupTeams, setGroupTeams] = useState({});
  const [lightDivisionData, setLightDivisionData] = useState({ matches: [], rounds: [] });
  const [hardDivisionData, setHardDivisionData] = useState({ matches: [], rounds: [] });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workbook) {
      processMenWorkbook(workbook);
    }
  }, [workbook]);

  const processMenWorkbook = (workbook) => {
    try {
      // Process Men's data (Vyrai tab)
      const menSheetIndex = workbook.SheetNames.findIndex(name => 
        name === 'Vyrai'
      );
      
      if (menSheetIndex !== -1) {
        const menSheet = workbook.Sheets[workbook.SheetNames[menSheetIndex]];
        
        // Process group stage data
        processMenData(menSheet);
        
        // Process bracket data
        const hardBracket = processBracketData(menSheet, 'men', 'hard');
        const lightBracket = processBracketData(menSheet, 'men', 'light');
        
        setHardDivisionData(hardBracket);
        setLightDivisionData(lightBracket);
      } else {
        setError("Men's sheet not found in workbook");
      }
    } catch (err) {
      console.error("Error processing men's data:", err);
      setError("Failed to process men's tournament data");
    }
  };

  const processMenData = (worksheet) => {
    // Convert to JSON with header: 1 to get arrays instead of objects
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    // Define court ranges for men's division
    const courtRanges = {
      '1 Aikštelė': [
        { startCell: 'C19', endCell: 'F22' }
      ],
      '2 Aikštelė': [
        { startCell: 'J19', endCell: 'M22' }
      ],
      '3 Aikštelė': [
        { startCell: 'Q16', endCell: 'T22' }
      ],
      '4 Aikštelė': [
        { startCell: 'C25', endCell: 'F31' }
      ],
      '5 Aikštelė': [
        { startCell: 'J25', endCell: 'M31' }
      ],
      '6 Aikštelė': [
        { startCell: 'Q25', endCell: 'T31' }
      ]
    };
    
    // Define the group configurations for men's division
    const groupConfigs = {
      'Group A': { 
        teamA: [
          { startCell: 'Q17', endCell: 'Q17' },
          { startCell: 'C19', endCell: 'C22' },
          { startCell: 'C31', endCell: 'C31' }
        ],
        teamB: [
          { startCell: 'T17', endCell: 'T17' },
          { startCell: 'F19', endCell: 'F22' },
          { startCell: 'F31', endCell: 'F31' }
        ],
        referee: [
          { startCell: 'U17', endCell: 'U17' },
          { startCell: 'G19', endCell: 'G22' },
          { startCell: 'G31', endCell: 'G31' }
        ],
        time: '10:00'
      },
      'Group B': { 
        teamA: [
          { startCell: 'Q16', endCell: 'Q16' },
          { startCell: 'J19', endCell: 'J22' },
          { startCell: 'J31', endCell: 'J31' }
        ],
        teamB: [
          { startCell: 'T16', endCell: 'T16' },
          { startCell: 'M19', endCell: 'M22' },
          { startCell: 'M31', endCell: 'M31' }
        ],
        referee: [
          { startCell: 'U16', endCell: 'U16' },
          { startCell: 'N19', endCell: 'N22' },
          { startCell: 'N31', endCell: 'N31' }
        ],
        time: '11:00'
      },
      'Group C': { 
        teamA: [
          { startCell: 'Q18', endCell: 'Q22' },
          { startCell: 'Q31', endCell: 'Q31' }
        ],
        teamB: [
          { startCell: 'T18', endCell: 'T22' },
          { startCell: 'T31', endCell: 'T31' }
        ],
        referee: [
          { startCell: 'U18', endCell: 'U22' },
          { startCell: 'U31', endCell: 'U31' }
        ],
        time: '12:00'
      },
      'Group D': { 
        teamA: { startCell: 'C25', endCell: 'C30' },
        teamB: { startCell: 'F25', endCell: 'F30' },
        referee: { startCell: 'G25', endCell: 'G30' },
        time: '13:00'
      },
      'Group E': { 
        teamA: { startCell: 'J25', endCell: 'J30' },
        teamB: { startCell: 'M25', endCell: 'M30' },
        referee: { startCell: 'N25', endCell: 'N30' },
        time: '14:00'
      },
      'Group F': { 
        teamA: { startCell: 'Q25', endCell: 'Q30' },
        teamB: { startCell: 'T25', endCell: 'T30' },
        referee: { startCell: 'U25', endCell: 'U30' },
        time: '15:00'
      }
    };
    
    // Process group data using shared utility function
    const { grouped, teamObj } = processGroupData(
      groupConfigs, 
      jsonData, 
      courtRanges, 
      getCellIndices, 
      determineCourtForGame
    );
    
    setMenData(grouped);
    setGroupTeams(prev => ({...prev, men: teamObj}));
  };

  if (loading) {
    return <div className="loading">Loading men's tournament schedule...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // Choose which content to display based on activeSubTab
  const renderTabContent = () => {
    switch(activeSubTab) {
      case 'group':
        return (
          <div className="tab-content active" id="group-tab">
            <h2>Group Stage Matches - Men</h2>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <label htmlFor="groupFilter"><strong>Filter by Group:</strong></label>
              <select id="groupFilter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All</option>
                {Object.keys(menData).map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            {Object.entries(menData).map(([groupName, matches]) =>
              renderGroupTable(groupName, matches, groupTeams, 'men', filter)
            )}
            {Object.keys(menData).length === 0 && (
              <div className="no-data-message">
                <p>No men's group stage data available yet.</p>
              </div>
            )}
          </div>
        );
      case 'light':
        return (
          <div className="tab-content active" id="light-tab">
            {lightDivisionData.matches && lightDivisionData.matches.length > 0 ? (
              <SearchableBracketWrapper 
                bracketData={lightDivisionData} 
                divisionName="Light Division - Men" 
              />
            ) : (
              <div>
                <h2 className="section-title">Light Division Bracket - Men</h2>
                <div className="no-data-message">
                  <p>Light division bracket data will be available closer to the tournament.</p>
                </div>
              </div>
            )}
          </div>
        );
      case 'hard':
        return (
          <div className="tab-content active" id="hard-tab">
            {hardDivisionData.matches && hardDivisionData.matches.length > 0 ? (
              <SearchableBracketWrapper 
                bracketData={hardDivisionData} 
                divisionName="Hard Division - Men" 
              />
            ) : (
              <div>
                <h2 className="section-title">Hard Division Bracket - Men</h2>
                <div className="no-data-message">
                  <p>Hard division bracket data will be available closer to the tournament.</p>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderTabContent()}
    </>
  );
};

export default MensSchedule;